import { useMemo } from 'react'
import { ClientClassroom } from 'common/utils/types/client-classroom'
import { Flow } from '../types'

/**
 * How many students/children the account already has. A purchase must cover them, so this drives
 * the seat minimum. Returns a lookup by flow; a value is null while its data still loads.
 */
export default function useSeatsUsed(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  classrooms: Record<string, any> | null
): (flow: Flow) => number | null {
  // Children on a parent account. Available as soon as the teacher record is.
  const childrenUsed = useMemo<number | null>(() => {
    if (!currentTeacher) return null
    return Object.keys(currentTeacher.childCodes?.() || {}).length
  }, [currentTeacher])

  // Unique students across the teacher's classrooms. null until the classrooms load.
  const studentsUsed = useMemo<number | null>(() => {
    if (!currentTeacher) return null
    const codes = currentTeacher.classroomCodes?.(null)
    if (!codes || Object.keys(codes).length === 0) return 0
    if (!classrooms) return null
    const used: Record<string, true> = {}
    Object.values(classrooms).forEach((c) => {
      try {
        Object.keys(new ClientClassroom(c).studentCodes() || {}).forEach(
          (code) => {
            used[code] = true
          }
        )
      } catch (e) {
        // ignore malformed classroom entries
      }
    })
    return Object.keys(used).length
  }, [currentTeacher, classrooms])

  return useMemo(
    () => (flow: Flow) => (flow === 'family' ? childrenUsed : studentsUsed),
    [childrenUsed, studentsUsed]
  )
}
